import React, { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Row,
  Col,
  Avatar,
  Typography,
  Space,
  Button,
  Dropdown,
  Modal,
  Drawer,
} from "antd";
import {
  ClockCircleOutlined,
  MoreOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import {
  getLeadCallByIdAPI,
  deleteLeadCallByIdAPI,
  getLeadCallsListAPI,
} from "../LeadsAPI";

import LeadCallForm from "./LeadCallForm";

const { Text } = Typography;

const LeadCallItem = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { leadId } = useParams();

  const selectedLanguage = localStorage.getItem("selectedlanguage") || "fr-FR";
  const leadCallsList = useSelector((state) => state.leads.leadCallList);
  const employeeList = useSelector((state) => state.lookup.employesList);
  const statusList = useSelector((state) => state.lookup.statusList);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingCall, setEditingCall] = useState(null);

  useEffect(() => {
    if (leadId) {
      dispatch(getLeadCallsListAPI(leadId));
    }
  }, [dispatch, leadId]);

  const getEmployeeNameById = (id) => {
    const user = employeeList.find((e) => e.id === id);
    return user ? `${user.firstName} ${user.lastName}` : "Unknown";
  };

  const statusMap = {
    8: { color: "#d46b08", label: "No Answer" },
    9: { color: "#52c41a", label: "Call Completed" },
    10: { color: "#faad14", label: "Follow-up Required" },
    11: { color: "#1677ff", label: "Voicemail Left" },
    12: { color: "#ff4d4f", label: "Busy/Line Engaged" },
  };

  const handleDelete = (call) => {
    Modal.confirm({
      title: intl.formatMessage({ id: "LeadCall.DeleteConfirm" }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      cancelText: intl.formatMessage({ id: "Common.No" }),
      okType: "danger",
      onOk() {
        dispatch(deleteLeadCallByIdAPI(call.id, leadId));
      },
    });
  };
  const handleEdit = (call) => {
    setEditingCall(call);
    setDrawerVisible(true);
  };
  const handleAddNew = () => {
    setEditingCall(null);
    setDrawerVisible(true);
  };
  const closeDrawer = () => {
    setDrawerVisible(false);
    setEditingCall(null);
  };
  const dropdownItems = (call) => [
    {
      key: "edit",
      label: (
        <span onClick={() => handleEdit(call)}>
          <EditOutlined /> <FormattedMessage id="Common.Edit" />
        </span>
      ),
    },
    {
      key: "delete",
      label: (
        <span onClick={() => handleDelete(call)}>
          <DeleteOutlined /> <FormattedMessage id="Common.Delete" />
        </span>
      ),
    },
  ];

  return (
    <>
      <Card
        size="small"
        title={<FormattedMessage id="LeadCall.CallLogs" />}
        extra={
          <Button type="link" onClick={handleAddNew}>
            <PlusCircleOutlined />
            <FormattedMessage id="LeadCall.AddNew" />
          </Button>
        }
      >
        {leadCallsList?.length > 0 ? (
          (() => {
            const call = leadCallsList[0];
            const status = statusMap[call.callStatusId];
            return (
              <Card key={call.id} className="activitytab">
                <div className="activitytab-dropdown">
                  <Space>
                    {call.followUpDate && (
                      <Tag
                        color="orange"
                        icon={<ClockCircleOutlined />}
                        style={{
                          fontWeight: "bold",
                          border: "1px solid #ffa940",
                          boxShadow: "0 0 4px rgba(255,169,64,0.3)",
                        }}
                      >
                        <FormattedMessage id="LeadCall.Follow" />:{" "}
                        {dayjs(call.followUpDate).format("DD MMM YYYY")}
                      </Tag>
                    )}

                    {status && (
                      <Tag color={status.color}>
                        {selectedLanguage === "fr-FR"
                          ? statusList.find((s) => s.id === call.callStatusId)
                              ?.nameFr
                          : status.label}
                      </Tag>
                    )}

                    <Dropdown menu={{ items: dropdownItems(call) }}>
                      <Button icon={<MoreOutlined />} />
                    </Dropdown>
                  </Space>
                </div>

                <Row gutter={12} align="middle">
                  <Col>
                    <Avatar size={36} icon={<UserOutlined />} />
                  </Col>
                  <Col flex="auto">
                    <Text strong>{getEmployeeNameById(call.createdBy)}</Text>{" "}
                    <br />
                    <Text type="secondary">
                      <FormattedMessage id="LeadCall.LoggedDate" />{" "}
                      {dayjs(call.createdDate).format("DD MMM YYYY, hh:mm a")}
                    </Text>
                  </Col>
                </Row>

                <div style={{ marginTop: 8 }}>
                  <p>{call.note}</p>
                </div>
              </Card>
            );
          })()
        ) : (
          <Text type="secondary">
            <FormattedMessage id="NoData" />
          </Text>
        )}
      </Card>

      <LeadCallForm
        call={editingCall}
        onClose={closeDrawer}
        drawerVisible={drawerVisible}
      />
    </>
  );
};

export default LeadCallItem;
